import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents } from 'react-leaflet';
import { MapPin, Navigation, Car, Search, Menu, X, Phone, Star, Clock, Users } from 'lucide-react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useGeolocation } from '../hooks/useGeolocation';
import { useNearbyTaxis } from '../hooks/useNearbyTaxis';

// Custom user location icon
const userIcon = new L.DivIcon({
  className: 'custom-div-icon',
  html: `
    <div class="relative">
      <div style="
        background-color: #3b82f6;
        width: 24px;
        height: 24px;
        border-radius: 50%;
        border: 3px solid #ffffff;
        box-shadow: 0 0 10px rgba(59, 130, 246, 0.5);
      "></div>
      <div style="
        position: absolute;
        top: -4px;
        left: -4px;
        right: -4px;
        bottom: -4px;
        border-radius: 50%;
        background: #3b82f6;
        opacity: 0.3;
        animation: pulse 2s infinite;
      "></div>
    </div>
  `,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
  popupAnchor: [0, -12],
});

// Custom taxi icon
const taxiIcon = new L.DivIcon({
  className: 'custom-div-icon',
  html: `
    <div style="
      background-color: #facc15;
      width: 24px;
      height: 24px;
      border-radius: 50%;
      border: 3px solid #ffffff;
      box-shadow: 0 0 15px rgba(250, 204, 21, 0.6);
      transition: transform 0.3s ease;
    "></div>
  `,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
  popupAnchor: [0, -12],
});

function MapUpdater({ center }: { center: L.LatLngExpression }) {
  const map = useMap();
  useEffect(() => {
    map.setView(center, map.getZoom());
  }, [center, map]);
  return null;
}

export default function Map() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [selectedTaxi, setSelectedTaxi] = useState<any>(null);
  const [mapCenter, setMapCenter] = useState<[number, number]>([41.0082, 28.9784]); // İstanbul merkezi
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);

  const { location, loading: locationLoading, getLocation } = useGeolocation();
  const { places, loading: placesLoading, searchNearbyTaxis } = useNearbyTaxis();

  useEffect(() => {
    // Tarayıcı dilini kontrol et ve varsayılan konumu ayarla
    const userLanguage = navigator.language.toLowerCase();
    if (userLanguage.startsWith('tr')) {
      setMapCenter([41.0082, 28.9784]); // Türkiye - İstanbul
    } else if (userLanguage.startsWith('de')) {
      setMapCenter([52.5200, 13.4050]); // Almanya - Berlin
    } else if (userLanguage.startsWith('fr')) {
      setMapCenter([48.8566, 2.3522]); // Fransa - Paris
    } else {
      setMapCenter([51.5074, -0.1278]); // Varsayılan - Londra
    }

    // Kullanıcı konumunu al
    getLocation();
  }, []);

  useEffect(() => {
    if (location && !location.error) {
      setMapCenter([location.latitude, location.longitude]);
      searchNearbyTaxis({ lat: location.latitude, lng: location.longitude });
    }
  }, [location]);

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    setIsSearching(true);

    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}`
      );
      const data = await response.json();

      if (data && data[0]) {
        const { lat, lon } = data[0];
        setMapCenter([parseFloat(lat), parseFloat(lon)]);
        await searchNearbyTaxis({ lat: parseFloat(lat), lng: parseFloat(lon) });
      }
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setIsSearching(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <div className="h-screen w-screen bg-[#1a1d24] relative overflow-hidden">
      {/* Sidebar Toggle Button */}
      <button
        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
        className="absolute top-4 left-4 z-50 p-2 bg-[#24272e] hover:bg-[#2d3039] rounded-lg transition-colors"
      >
        {isSidebarOpen ? (
          <X className="w-6 h-6 text-white" />
        ) : (
          <Menu className="w-6 h-6 text-white" />
        )}
      </button>

      {/* Sidebar */}
      <div
        ref={sidebarRef}
        className={`absolute top-0 left-0 h-full w-80 bg-[#24272e] transform transition-transform duration-300 ease-in-out z-40 ${
          isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="p-6 space-y-6">
          {/* Search */}
          <div className="pt-8">
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Konum ara..."
                className="w-full bg-[#1a1d24] text-white px-4 py-3 rounded-xl border border-white/10 focus:border-yellow-400/50 focus:ring-2 focus:ring-yellow-400/20 focus:outline-none placeholder-gray-500 pl-11"
              />
              <Search className="w-5 h-5 text-gray-400 absolute left-4 top-3.5" />
            </div>
          </div>

          {/* Current Location */}
          <button
            onClick={() => getLocation()}
            disabled={locationLoading}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-500 hover:to-yellow-600 text-black font-medium rounded-xl transition-colors disabled:opacity-50"
          >
            {locationLoading ? (
              <Clock className="w-5 h-5 animate-spin" />
            ) : (
              <Navigation className="w-5 h-5" />
            )}
            <span>Konumumu Bul</span>
          </button>

          {/* Nearby Taxis */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-white font-medium">Yakındaki Taksiler</h2>
              <span className="text-sm text-gray-400">{places.length} taksi</span>
            </div>

            <div className="space-y-3 max-h-[calc(100vh-280px)] overflow-y-auto pr-2">
              {places.map((place, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setSelectedTaxi(place);
                    setMapCenter([
                      parseFloat(place.latitude as string),
                      parseFloat(place.longitude as string)
                    ]);
                  }}
                  className={`w-full text-left p-4 rounded-xl transition-colors ${
                    selectedTaxi === place
                      ? 'bg-yellow-400/10 border border-yellow-400/20'
                      : 'bg-[#1a1d24] hover:bg-[#2d3039] border border-white/5'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${
                      selectedTaxi === place ? 'bg-yellow-400/10' : 'bg-[#2d3039]'
                    }`}>
                      <Car className={`w-5 h-5 ${
                        selectedTaxi === place ? 'text-yellow-400' : 'text-gray-400'
                      }`} />
                    </div>
                    <div>
                      <h3 className="font-medium text-white">{place.name}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        <div className="flex items-center gap-1">
                          <MapPin className="w-3.5 h-3.5 text-gray-400" />
                          <span className="text-sm text-gray-400">{place.distance}</span>
                        </div>
                        {place.rating && (
                          <div className="flex items-center gap-1">
                            <Star className="w-3.5 h-3.5 text-yellow-400 fill-current" />
                            <span className="text-sm text-yellow-400">{place.rating}</span>
                          </div>
                        )}
                      </div>
                      <p className="text-sm text-gray-400 mt-1 line-clamp-2">{place.address}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Map */}
      <div className={`absolute inset-0 transition-all duration-300 ${
        isSidebarOpen ? 'ml-80' : 'ml-0'
      }`}>
        <MapContainer
          center={mapCenter}
          zoom={14}
          style={{ height: '100%', width: '100%' }}
          zoomControl={false}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://cartodb-basemaps-{s}.global.ssl.fastly.net/dark_all/{z}/{x}/{y}.png"
          />

          <MapUpdater center={mapCenter} />

          {location && !location.error && (
            <Marker
              position={[location.latitude, location.longitude]}
              icon={userIcon}
            >
              <Popup>
                <div className="text-center">
                  <p className="font-medium">Konumunuz</p>
                </div>
              </Popup>
            </Marker>
          )}

          {places.map((place, index) => (
            <Marker
              key={index}
              position={[
                parseFloat(place.latitude as string),
                parseFloat(place.longitude as string)
              ]}
              icon={taxiIcon}
              eventHandlers={{
                click: () => setSelectedTaxi(place)
              }}
            >
              <Popup>
                <div className="p-2">
                  <h3 className="font-medium">{place.name}</h3>
                  <p className="text-sm text-gray-600 mt-1">{place.address}</p>
                  <div className="flex items-center justify-between mt-2">
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4 text-gray-500" />
                      <span className="text-sm">{place.distance}</span>
                    </div>
                    {place.rating && (
                      <div className="flex items-center gap-1">
                        <Star className="w-4 h-4 text-yellow-500 fill-current" />
                        <span className="text-sm">{place.rating}</span>
                      </div>
                    )}
                  </div>
                  {place.phone && (
                    <a
                      href={`tel:${place.phone}`}
                      className="block w-full mt-3 bg-yellow-400 hover:bg-yellow-500 text-black text-sm font-medium rounded-lg px-4 py-2 text-center transition-colors"
                    >
                      <Phone className="w-4 h-4 inline-block mr-2" />
                      Taksi Çağır
                    </a>
                  )}
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>
    </div>
  );
}